/**
 * Where every page sits, and therefore how to get back out of it.
 *
 * Both portals are shallow by design, but "shallow" still leaves plenty of
 * pages two or three levels down — an invoice reached from a tenant, a report
 * reached from a folder, an account reached from a search — and until now the
 * only way back was the browser button or the address bar. A handful of pages
 * had hand-written trails; most had none, and the ones that existed disagreed
 * about wording.
 *
 * So the trail is DATA, in one table, rather than markup repeated across sixty
 * files. A page cannot forget to render it (the shells do that), cannot word it
 * differently from its neighbour, and cannot drift from the routes — a test
 * reads App.tsx and fails if a shell route has no entry here.
 *
 * Three roots, because there are three portals. Anything under /admin climbs to
 * the Control Center, anything under /portal to the tenant's own home page, and
 * everything else to the landlord dashboard. Nothing ever climbs from one into
 * another: they are separate places, and a crumb that crossed between them
 * would suggest otherwise — most consequentially in the portal, where the trail
 * is the only navigation a tenant sees above the fold on a phone.
 */

export interface Crumb {
  label: string;
  /** Absent on the current page, which is text rather than a link. */
  to?: string;
}

interface Node {
  label: string;
  /** The pattern one level up. Absent only on the two roots. */
  parent?: string;
}

/** The landlord app. */
const ROOT_APP = '/';
/** The Control Center. */
const ROOT_CC = '/admin';
/** The Tenant Portal. */
const ROOT_PORTAL = '/portal';
/** The Owner Portal. */
const ROOT_OWNER = '/owner';
/** Explore listings somebody has posted. */
const ROOT_PROVIDER = '/my-listings';

/**
 * Keyed by route pattern, `:name` marking a parameter.
 *
 * Parents are chosen by where a person came FROM, not by URL shape. Payments
 * and arrears sit under Rent & Payments because that is the sidebar
 * destination that owns them; maintenance and expenses stay at the top level
 * because /operations only redirects and a crumb pointing at a redirect is a
 * trapdoor, not a way back.
 */
const NODES: Record<string, Node> = {
  // ---- landlord app -------------------------------------------------------
  [ROOT_APP]: { label: 'Home' },
  '/dashboard': { label: 'Dashboard', parent: ROOT_APP },
  '/dashboard/collections': { label: 'Collections', parent: '/dashboard' },
  '/dashboard/owners': { label: 'Owners', parent: '/dashboard' },
  '/dashboard/cash': { label: 'Cash and M-Pesa', parent: '/dashboard' },
  '/dashboard/renewals': { label: 'Renewals', parent: '/dashboard' },
  '/dashboard/vacancy': { label: 'Vacancy and letting', parent: '/dashboard' },
  '/dashboard/maintenance': { label: 'Maintenance', parent: '/dashboard' },

  '/properties': { label: 'Properties', parent: ROOT_APP },
  '/properties/new': { label: 'Add property', parent: '/properties' },
  '/properties/:id': { label: 'Property', parent: '/properties' },
  '/properties/:id/edit': { label: 'Edit', parent: '/properties/:id' },

  '/units': { label: 'Units', parent: ROOT_APP },
  '/units/new': { label: 'Add unit', parent: '/units' },
  '/units/edit': { label: 'Edit unit', parent: '/units' },
  '/units/:id/edit': { label: 'Edit unit', parent: '/units' },

  '/tenants': { label: 'Tenants', parent: ROOT_APP },
  '/tenants/new': { label: 'Add tenant', parent: '/tenants' },
  '/tenants/:id': { label: 'Tenant', parent: '/tenants' },
  '/tenants/:id/edit': { label: 'Edit', parent: '/tenants/:id' },

  '/tenancies': { label: 'Tenancies', parent: ROOT_APP },
  '/tenancies/new': { label: 'New tenancy', parent: '/tenancies' },

  '/owners': { label: 'Owners', parent: ROOT_APP },
  '/owners/:id': { label: 'Owner', parent: '/owners' },

  '/leases': { label: 'Leases', parent: ROOT_APP },
  '/leases/new': { label: 'Record a lease', parent: '/leases' },
  '/agreements': { label: 'Master agreements', parent: '/leases' },
  '/agreements/new': { label: 'New master agreement', parent: '/agreements' },
  '/agreements/:id': { label: 'Master agreement', parent: '/agreements' },
  '/leases/:id': { label: 'Lease', parent: '/leases' },
  '/rent': { label: 'Rent & payments', parent: ROOT_APP },
  '/payments': { label: 'Payments', parent: '/rent' },
  '/payments/new': { label: 'Record payment', parent: '/payments' },
  '/payments/mpesa': { label: 'M-Pesa', parent: '/payments' },
  '/payments/reconcile': { label: 'Reconcile', parent: '/payments' },
  '/arrears': { label: 'Arrears', parent: '/rent' },
  '/deposits': { label: 'Deposits', parent: '/rent' },
  '/deposits/:tenancyId/settle': { label: 'Settle deposit', parent: '/deposits' },
  '/receipts/:id': { label: 'Receipt', parent: '/payments' },

  '/invoices': { label: 'Invoices', parent: ROOT_APP },
  '/credit-notes': { label: 'Credit notes', parent: '/invoices' },
  '/invoices/new': { label: 'New invoice', parent: '/invoices' },
  '/invoices/:id': { label: 'Invoice', parent: '/invoices' },
  '/invoices/:id/edit': { label: 'Edit', parent: '/invoices/:id' },

  '/vacancies': { label: 'Vacancies', parent: ROOT_APP },
  '/applications': { label: 'Applications', parent: '/vacancies' },
  '/listing-bundles': { label: 'Advertising allowance', parent: '/vacancies' },
  '/listings/new': { label: 'New listing', parent: '/vacancies' },
  '/listings/:id': { label: 'Listing', parent: '/vacancies' },
  '/listings/:id/promote': { label: 'Promote', parent: '/listings/:id' },

  '/sale-listings': { label: 'Properties for sale', parent: ROOT_APP },
  '/sale-packages': { label: 'Advertising packages', parent: '/sale-listings' },
  '/sale-demand': { label: 'How your adverts are doing', parent: '/sale-listings' },
  '/sale-enquiries': { label: 'Buyer enquiries', parent: '/sale-listings' },
  '/sale-listings/new': { label: 'List a property', parent: '/sale-listings' },
  '/sale-listings/:id': { label: 'Sale listing', parent: '/sale-listings' },
  '/sale-listings/:id/pay': { label: 'Publish', parent: '/sale-listings/:id' },
  '/sale-listings/:id/verification': { label: 'Get it checked', parent: '/sale-listings/:id' },

  '/reports': { label: 'Reports', parent: ROOT_APP },
  '/reports/statement': { label: 'Tenant statement', parent: '/reports' },

  // /operations and /settings only redirect. They are listed so the route
  // audit passes and so a stray link to either still resolves, but no page
  // renders at them.
  '/operations': { label: 'Operations', parent: ROOT_APP },
  '/inspections': { label: 'Inspections', parent: ROOT_APP },
  '/inspections/:id': { label: 'Inspection', parent: '/inspections' },
  '/maintenance': { label: 'Maintenance', parent: ROOT_APP },
  '/messages': { label: 'Messages', parent: ROOT_APP },
  '/expenses': { label: 'Expenses', parent: ROOT_APP },
  '/utilities': { label: 'Utilities', parent: ROOT_APP },
  '/utilities/readings': { label: 'Meter readings', parent: '/utilities' },
  '/utilities/tariffs': { label: 'Tariffs', parent: '/utilities' },
  '/utilities/meters': { label: 'Meters', parent: '/utilities' },
  '/documents': { label: 'Documents', parent: ROOT_APP },
  '/data': { label: 'Import & export', parent: ROOT_APP },
  '/more': { label: 'More', parent: ROOT_APP },
  '/alerts': { label: 'Alerts', parent: ROOT_APP },

  '/calendar': { label: 'Calendar', parent: ROOT_APP },
  '/appointments': { label: 'Appointments', parent: '/calendar' },

  '/settings': { label: 'Settings', parent: ROOT_APP },
  '/settings/subscription': { label: 'Subscription', parent: '/settings' },
  '/settings/users': { label: 'Users', parent: '/settings' },
  '/settings/notifications': { label: 'Notifications', parent: '/settings' },
  '/settings/statements': { label: 'Statements', parent: '/settings' },
  '/insights': { label: 'What the AI found' },
  '/settings/calendar': { label: 'Calendar', parent: '/settings' },
  '/settings/agreement': { label: 'NyumbaSuite agreement', parent: '/settings' },
  '/settings/payments': { label: 'Payment collection', parent: '/settings' },
  '/settings/account': { label: 'Account', parent: '/settings' },

  // ---- Tenant Portal ------------------------------------------------------
  // Flat by design. A tenant's whole portal is eight pages, all one level down,
  // so every trail here is exactly two entries: home, then where you are.
  [ROOT_PORTAL]: { label: 'Home' },

  '/portal/tenancy': { label: 'My home', parent: ROOT_PORTAL },
  '/portal/rent': { label: 'Rent', parent: ROOT_PORTAL },
  '/portal/statement': { label: 'My statement', parent: ROOT_PORTAL },
  '/portal/invoices': { label: 'Invoices', parent: ROOT_PORTAL },
  '/portal/payments': { label: 'Payments', parent: ROOT_PORTAL },
  '/portal/maintenance': { label: 'Repairs', parent: ROOT_PORTAL },
  '/portal/utilities': { label: 'Water & power', parent: ROOT_PORTAL },
  '/portal/documents': { label: 'Documents', parent: ROOT_PORTAL },
  '/portal/profile': { label: 'My details', parent: ROOT_PORTAL },
  '/portal/more': { label: 'More', parent: ROOT_PORTAL },

  // ---- Owner Portal -------------------------------------------------------
  // Flat like the tenant portal: a dashboard and three places one level down.
  [ROOT_OWNER]: { label: 'Dashboard' },

  '/owner/properties': { label: 'My properties', parent: ROOT_OWNER },
  '/owner/properties/:id': { label: 'Property', parent: '/owner/properties' },
  '/owner/financials': { label: 'Financials', parent: ROOT_OWNER },
  '/owner/reports': { label: 'Reports', parent: ROOT_OWNER },

  // ---- Explore listings somebody has posted -------------------------------
  // One page, so the trail is never rendered — but the root is declared so the
  // route audit in tests/navigation.test.ts can see it, and so a second page
  // added here has a parent waiting for it.
  [ROOT_PROVIDER]: { label: 'My listings' },

  // ---- Control Center -----------------------------------------------------
  [ROOT_CC]: { label: 'Control Center' },

  '/admin/accounts': { label: 'User management', parent: ROOT_CC },
  '/admin/agreements': { label: 'Agreements & Contracts', parent: ROOT_CC },
  '/admin/agreements/reports': { label: 'Agreement reports', parent: '/admin/agreements' },
  '/admin/agreements/reports/:code': { label: 'Report', parent: '/admin/agreements/reports' },
  '/admin/team': { label: 'Control Centre team', parent: ROOT_CC },
  '/admin/accounts/:id': { label: 'Account', parent: '/admin/accounts' },
  '/admin/listings': { label: 'Property & listings', parent: ROOT_CC },
  '/admin/sale-listings': { label: 'Properties for sale', parent: ROOT_CC },
  '/admin/sale-payments': { label: 'Sale listing payments', parent: ROOT_CC },
  '/admin/sale-packages': { label: 'Advertising packages', parent: ROOT_CC },
  '/admin/sale-verifications': { label: 'Seller verification', parent: ROOT_CC },
  '/admin/listing-promotions': { label: 'Listing promotions', parent: ROOT_CC },
  '/admin/listing-bundles': { label: 'Advertising bundles', parent: ROOT_CC },
  '/admin/marketplace': { label: 'Explore', parent: ROOT_CC },
  '/admin/subscriptions': { label: 'Subscriptions', parent: ROOT_CC },
  '/admin/alerts': { label: 'Alerts', parent: '/admin' },
  '/admin/quote-requests': { label: 'Quote requests', parent: ROOT_CC },
  '/admin/quotations': { label: 'Quotations', parent: ROOT_CC },
  '/admin/finance': { label: 'Finance', parent: ROOT_CC },
  '/admin/content': { label: 'Content & marketing', parent: ROOT_CC },
  '/admin/reports': { label: 'Reports', parent: ROOT_CC },
  '/admin/reports/:code': { label: 'Report', parent: '/admin/reports' },
  '/admin/settings': { label: 'System settings', parent: ROOT_CC },
  '/admin/features': { label: 'Feature locking', parent: ROOT_CC },
  '/admin/roles': { label: 'Roles & permissions', parent: ROOT_CC },
  '/admin/ai': { label: 'AI Command Center', parent: ROOT_CC },
  '/admin/audit': { label: 'Audit log', parent: ROOT_CC },
};

/** Every pattern this table knows, for the route audit in the test suite. */
export const CRUMB_PATTERNS = Object.keys(NODES);

function segments(path: string): string[] {
  return path.split('/').filter(Boolean);
}

/**
 * The pattern a concrete path matches, or null.
 *
 * Literal segments beat parameters, so /properties/new is the add form rather
 * than a property whose id is the word "new".
 */
export function matchPattern(pathname: string): string | null {
  const want = segments(pathname.split('?')[0]!.split('#')[0]!);
  if (want.length === 0) return ROOT_APP;

  let best: string | null = null;
  let bestLiterals = -1;

  for (const pattern of CRUMB_PATTERNS) {
    const have = segments(pattern);
    if (have.length !== want.length) continue;

    let literals = 0;
    let ok = true;
    for (let i = 0; i < have.length; i++) {
      const p = have[i]!;
      if (p.startsWith(':')) continue;
      if (p !== want[i]) { ok = false; break; }
      literals++;
    }
    if (ok && literals > bestLiterals) { best = pattern; bestLiterals = literals; }
  }
  return best;
}

/** True when the path belongs to the Control Center rather than the app. */
export function isPlatformPath(pathname: string): boolean {
  return pathname === ROOT_CC || pathname.startsWith(`${ROOT_CC}/`);
}

/**
 * The trail for a path, root first, current page last.
 *
 * `leaf` renames the final crumb — a detail page passes the record's name, so
 * the trail reads "Properties / Kilimani Court" rather than "Properties /
 * Property". It is ignored on pages that are not a record, so a page cannot
 * quietly rewrite a section's name.
 *
 * Returns an empty array for a portal's own root: a trail whose only entry is
 * the page you are already on is noise.
 */
export function trailFor(pathname: string, leaf?: string | null): Crumb[] {
  const pattern = matchPattern(pathname);
  if (!pattern) return [];

  const chain: string[] = [];
  for (let at: string | undefined = pattern; at; at = NODES[at]?.parent) {
    if (chain.includes(at)) break;   // a cycle would otherwise hang the render
    chain.push(at);
  }
  chain.reverse();
  if (chain.length < 2) return [];

  return chain.map((p, i) => {
    const last = i === chain.length - 1;
    const named = last && leaf && p.includes('/:');
    return {
      label: named ? leaf : NODES[p]!.label,
      // The current page is not a link to itself. Ancestors keep their
      // pattern only when it is concrete — a parameterised ancestor is
      // rebuilt from the path being viewed, so /properties/:id/edit can
      // link back to the property it is editing.
      to: last ? undefined : concrete(p, pattern, pathname),
    };
  });
}

/** Fills a parameterised ancestor from the path currently being viewed. */
function concrete(ancestor: string, pattern: string, pathname: string): string {
  if (!ancestor.includes('/:')) return ancestor;
  const have = segments(pathname);
  const want = segments(ancestor);
  const from = segments(pattern);
  const out = want.map((s, i) => (s.startsWith(':') && from[i] === s ? have[i] ?? s : s));
  return `/${out.join('/')}`;
}
